import {
  type LoginCredentials,
  type AuthResponse,
  type LoginApiResponse,
  type User,
  AuthErrorCode,
  AuthError,
} from '../../types/auth';
import { Environment } from '../../config/environment';
import { http } from '../../api/http';

/**
 * Authentication Service
 * 
 * Handles all authentication-related API calls and business logic.
 * This service is framework-agnostic and can be easily tested.
 */
export class AuthService {
  private static readonly TOKEN_KEY = 'ACCESS_TOKEN';
  private static readonly USER_KEY = 'user_data';

  /**
   * Decode JWT token to extract payload
   */
  private static decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Authenticates user with username and password
   */
  public static async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await http.post(Environment.API_ENDPOINTS.AUTH.LOGIN, {
        username: credentials.username,
        password: credentials.password,
      });

      const loginResponse: LoginApiResponse = response.data;

      // Store token in localStorage/sessionStorage 
      if (credentials.rememberMe) {
        localStorage.setItem(this.TOKEN_KEY, loginResponse.token);
      } else {
        sessionStorage.setItem(this.TOKEN_KEY, loginResponse.token);
      }
      
      console.log('Token stored:', loginResponse.token);
      console.log('Remember me:', credentials.rememberMe);

      // Decode token to extract user info
      const decoded = this.decodeToken(loginResponse.token);
      console.log('Decoded token:', decoded);

      // Extract role from token (handle full claim name or short name)
      const role = decoded?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']
                || decoded?.role
                || 'user';

      // Extract user id from token
      const userId = decoded?.UserId
                  || decoded?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']
                  || '0';

      // Extract name from token
      const name = decoded?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name']
                || decoded?.sub
                || credentials.username;

      // Create user object from token data
      const user: User = {
        id: userId,
        email: credentials.username,
        name: name,
        role: role,
        isActive: true,
        lastLoginAt: new Date(),
      };

      // Store user data
      if (credentials.rememberMe) {
        localStorage.setItem(this.USER_KEY, JSON.stringify(user));
      } else {
        sessionStorage.setItem(this.USER_KEY, JSON.stringify(user));
      }
      
      console.log('User stored:', user);

      return {
        token: loginResponse.token,
      };
    } catch (error: any) {
      if (error instanceof AuthError) {
        throw error;
      }
      console.error('Login error:', error);

      // Handle axios errors with specific status codes
      if (error.response) {
        const status = error.response.status;

        if (status === 401) {
          throw new AuthError('Invalid username or password', AuthErrorCode.INVALID_CREDENTIALS);
        }

        if (status === 500) {
          throw new AuthError('Server error. Please contact system administrator.', AuthErrorCode.SERVER_ERROR);
        }

        if (status >= 400 && status < 500) {
          throw new AuthError('Invalid request. Please check your credentials.', AuthErrorCode.INVALID_CREDENTIALS);
        }

        throw new AuthError(`Server error (${status}). Please try again later.`, AuthErrorCode.SERVER_ERROR);
      }

      // Handle network errors (no response)
      if (error.request) {
        throw new AuthError('Cannot connect to server. Please check if the backend is running.', AuthErrorCode.NETWORK_ERROR);
      }

      throw new AuthError('Network error occurred', AuthErrorCode.NETWORK_ERROR);
    }
  }

  /**
   * Logs out the current user
   */
  public static async logout(): Promise<void> {
    try {
      // Clear stored tokens and user data
      this.clearStoredAuth();
    } catch (error) {
      console.error('Logout error:', error);
      this.clearStoredAuth();
    }
  }


  /**
   * Gets the current access token
   */
  public static getAccessToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY) || sessionStorage.getItem(this.TOKEN_KEY);
  }


  /**
   * Gets the stored user data
   */
  public static getStoredUser(): User | null {
    const userData = localStorage.getItem(this.USER_KEY) || sessionStorage.getItem(this.USER_KEY);
    
    if (!userData) {
      return null;
    }

    try {
      return JSON.parse(userData) as User;
    } catch {
      return null;
    }
  }

  /**
   * Checks if user is currently authenticated
   */
  public static isAuthenticated(): boolean {
    const token = this.getAccessToken();
    const user = this.getStoredUser();
    
    console.log('Auth check - Token:', !!token, 'User:', !!user);
    console.log('Token value:', token);
    console.log('User value:', user);
    
    return !!(token && user);
  }

  /**
   * Clears all stored authentication data
   */
  private static clearStoredAuth(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.USER_KEY);
  }

}